import { useEffect, useState, useRef, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { useStore } from './store/useStore'
import { useAuth } from './contexts/AuthContext'
import { useAdminAuth } from './hooks/useAdminAuth'
import ProjectList from './components/ProjectList'
import TodoList from './components/TodoList'
import Header from './components/Header'
import LoginForm from './components/LoginForm'
import UpgradeBanner from './components/UpgradeBanner'
import AnnouncementModal from './components/AnnouncementModal'
import BottomProjectSelector from './components/BottomProjectSelector'
import GracePeriodBanner from './components/GracePeriodBanner'
import NetworkStatus from './components/NetworkStatus'
import LoadingSpinner from './components/LoadingSpinner'
import { NetworkProvider } from './contexts/NetworkContext'
import { isNativeApp, isIOS } from './lib/platform'
import { BILLING_ENABLED, TEAMS_ENABLED } from './config/features'
import { PushNotificationProvider } from './contexts/PushNotificationContext'
import { DemoProvider } from './contexts/DemoContext'

// Lazy load heavy components
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({ default: m.AdminDashboard })))
const UserSettings = lazy(() => import('./components/UserSettings'))
const PricingPage = lazy(() => import('./components/PricingPage').then(m => ({ default: m.PricingPage })))
const BillingManagement = lazy(() => import('./components/BillingManagement').then(m => ({ default: m.BillingManagement })))
const ReferralProgram = lazy(() => import('./components/ReferralProgram').then(m => ({ default: m.ReferralProgram })))
const UserDashboardPage = lazy(() => import('./components/UserDashboardPage').then(m => ({ default: m.UserDashboardPage })))
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService').then(m => ({ default: m.TermsOfService })))
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))
const CommercialTransactionsActPage = lazy(() => import('./pages/legal/CommercialTransactionsActPage'))
const FAQPage = lazy(() => import('./pages/FAQPage'))
const HelpPage = lazy(() => import('./pages/HelpPage'))
const HelpVideosPage = lazy(() => import('./pages/HelpVideosPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const ChangelogPage = lazy(() => import('./components/ChangelogPage'))
const ImageManagement = lazy(() => import('./components/ImageManagement'))
const FileManagement = lazy(() => import('./components/FileManagement'))
const AdminTierSettings = lazy(() => import('./components/AdminTierSettings'))
const AdminContactPage = lazy(() => import('./pages/AdminContactPage'))
const AdminAIUsagePage = lazy(() => import('./pages/AdminAIUsagePage'))
const AdminAISettings = lazy(() => import('./components/AdminAISettings'))
const AdminStaticContentPage = lazy(() => import('./pages/AdminStaticContentPage'))
const LandingPage = lazy(() => import('./components/LandingPage').then(m => ({ default: m.LandingPage })))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const TeamManagement = lazy(() => import('./components/team/TeamManagement').then(m => ({ default: m.TeamManagement })))
const AcceptTeamInvitation = lazy(() => import('./pages/AcceptTeamInvitation').then(m => ({ default: m.AcceptTeamInvitation })))
const ShareInvitePage = lazy(() => import('./pages/ShareInvitePage'))
// GanttChart V2 (開発中 - feature/milestone-gantt ブランチ)
const GanttChartV2Page = lazy(() => import('./pages/GanttChartV2Page'))
// デモモードページ（本番DBに一切影響しない）
const DemoPage = lazy(() => import('./pages/DemoPage'))
// デバッグコンポーネント（本番環境では無効化）
// import { AdminDebug } from './components/AdminDebug'
// import { SimpleAdminTest } from './components/SimpleAdminTest'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { initializeFeedback } from './lib/feedback'
import { isNewUser, createTutorialProject, applyNewUserDefaults } from './services/tutorialService'
import { isTourCompletedLocal, checkTourCompletedFromDB, markTourCompleted } from './components/OnboardingTour'
import WelcomeVideoModal from './components/WelcomeVideoModal'
import { useSystemDarkMode } from './hooks/useSystemDarkMode'
import { useOfflineSync } from './hooks/useOfflineSync'
import { useRealtimeSync } from './hooks/useRealtimeSync'
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts'
import KeyboardShortcutsModal from './components/KeyboardShortcutsModal'
import { usePullToRefresh } from './hooks/usePullToRefresh'
import { PullToRefreshIndicator } from './components/PullToRefreshIndicator'

// UI実験フラグ - 下部プロジェクト選択UIを有効にする
const USE_BOTTOM_PROJECT_SELECTOR = true

function Dashboard() {
  // オフライン同期フックを使用
  useOfflineSync()
  // リアルタイム同期フックを使用（チームメンバー間でのデータ同期）
  useRealtimeSync()
  const { error, clearError, ganttFullscreenMode } = useStore()
  const { user } = useAuth()
  const { isAdmin, loading: adminLoading } = useAdminAuth()
  const { t } = useTranslation()
  const [showUserSettings, setShowUserSettings] = useState(false)
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false)
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false)
  // 初回表示確認を一度だけ行うためのref
  const welcomeCheckedRef = useRef(false)

  // キーボードショートカットを設定
  useKeyboardShortcuts({
    enabled: true,
    shortcuts: [
      {
        key: '?',
        shift: true,
        description: 'ショートカット一覧を表示',
        action: () => setShowShortcutsHelp(true),
      },
      {
        key: 'Escape',
        description: 'モーダルを閉じる',
        action: () => {
          setShowShortcutsHelp(false)
          setShowUserSettings(false)
        },
      },
    ],
  })

  // Pull-to-refresh（iOSネイティブアプリ用）
  const handlePullToRefresh = async () => {
    // データを再取得
    const { selectedProjectId } = useStore.getState()
    await Promise.all([
      useStore.getState().fetchProjects(),
      useStore.getState().fetchTodos(selectedProjectId ?? undefined),
      useStore.getState().fetchTodoStats(selectedProjectId ?? undefined)
    ])
  }

  const { pullDistance, pullProgress, isRefreshing } = usePullToRefresh({
    onRefresh: handlePullToRefresh,
    threshold: 80,
    enabled: true
  })

  // Initialize audio context on first user interaction
  useEffect(() => {
    const handleFirstInteraction = async () => {
      await initializeFeedback()
      // Remove listeners after first interaction
      document.removeEventListener('click', handleFirstInteraction)
      document.removeEventListener('touchstart', handleFirstInteraction)
      document.removeEventListener('pointerdown', handleFirstInteraction)
    }

    document.addEventListener('click', handleFirstInteraction)
    document.addEventListener('touchstart', handleFirstInteraction)
    document.addEventListener('pointerdown', handleFirstInteraction)

    return () => {
      document.removeEventListener('click', handleFirstInteraction)
      document.removeEventListener('touchstart', handleFirstInteraction)
      document.removeEventListener('pointerdown', handleFirstInteraction)
    }
  }, [])

  useEffect(() => {
    const loadData = async () => {
      if (user) {
        // ログイン中のユーザーIDを確認
        const storedUserId = localStorage.getItem('tastack-current-user-id')

        // ユーザーが変わった場合は必ずクリア
        if (storedUserId && storedUserId !== user.id) {
          console.log('User changed, clearing store immediately')
          // まず即座にストアをクリア（画面に古いデータが表示されないように）
          useStore.getState().clearStore()
          localStorage.removeItem('tastack-storage')
        }

        // 現在のユーザーIDを保存
        localStorage.setItem('tastack-current-user-id', user.id)

        // 新規ユーザーの場合、チュートリアルプロジェクトを作成
        const userIsNew = await isNewUser(user.id)
        if (userIsNew) {
          console.log('New user detected, creating tutorial project...')
          // デフォルト設定を適用（完了タスクを表示）
          applyNewUserDefaults()
          // チュートリアルプロジェクトを作成
          const tutorialProjectId = await createTutorialProject(user.id)
          if (tutorialProjectId) {
            console.log('Tutorial project created:', tutorialProjectId)
            // チュートリアルプロジェクトを選択状態にする
            useStore.getState().setSelectedProjectId(tutorialProjectId)
          }
        }

        // 初回ユーザーの場合のみウェルカム動画を表示（アカウント単位で1回のみ）
        // 既にチェック済みの場合はスキップ（セッション中に1回だけ）
        if (!welcomeCheckedRef.current) {
          welcomeCheckedRef.current = true
          // まずローカルキャッシュをチェック（高速）
          if (!isTourCompletedLocal()) {
            // ローカルにない場合はデータベースをチェック
            const tourCompleted = await checkTourCompletedFromDB(user.id)
            if (!tourCompleted) {
              // 少し遅延してウェルカム動画モーダルを表示（UIがレンダリングされるのを待つ）
              setTimeout(() => {
                setShowWelcomeVideo(true)
              }, 1000)
            }
          }
        }

        // データをfetch（selectedProjectIdを尊重）
        const { selectedProjectId } = useStore.getState()

        // まずプロジェクトをfetch（完了を待つ）
        await useStore.getState().fetchProjects()

        // プロジェクト取得後にタスクをfetch
        await useStore.getState().fetchTodos(selectedProjectId ?? undefined)
        await useStore.getState().fetchTodoStats(selectedProjectId ?? undefined)
      } else {
        // ログアウト状態の場合、ストアとuser-idをクリア
        useStore.getState().clearStore()
        localStorage.removeItem('tastack-current-user-id')
        localStorage.removeItem('tastack-storage')
      }
    }

    loadData()
  }, [user])

  // エラーが発生した時にコンソールに出力
  useEffect(() => {
    if (error) {
      console.error('Store error detected:', error, 'Type:', typeof error)
    }
  }, [error])

  // 管理者ステータスのチェック中はローディング表示
  if (adminLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // 管理者の場合は管理ダッシュボードのみを表示
  if (isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header onOpenSettings={() => setShowUserSettings(true)} />
        <div className="container mx-auto px-4 py-4">
          <AdminDashboard />
        </div>
        <UserSettings
          isOpen={showUserSettings}
          onClose={() => setShowUserSettings(false)}
        />
      </div>
    )
  }

  return (
    <div className="app-fixed-layout h-screen-safe flex flex-col bg-gray-50">
      {/* Pull-to-refreshインジケーター（iOSネイティブアプリ用） */}
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        pullProgress={pullProgress}
        isRefreshing={isRefreshing}
      />

      {/* ヘッダー部分 - PCはflexレイアウト内、モバイルはfixedなのでスペーサーを追加 */}
      <div className="flex-shrink-0">
        <Header
          onOpenSettings={() => setShowUserSettings(true)}
          hideOnMobile
        />
        {/* モバイル: ヘッダーは非表示（機能は下部バーに集約）。
            ネイティブアプリのみステータスバー塗りつぶし分のスペーサーを確保 */}
        {isNativeApp() && (
          <div
            className="lg:hidden"
            style={{
              height: isIOS() ? 'env(safe-area-inset-top, 0px)' : '24px'
            }}
          />
        )}

        {/* Network Status Banner */}
        <NetworkStatus />

        {/* Grace Period Banner */}
        <div className="container mx-auto px-2 sm:px-4 mt-1 sm:mt-4">
          <GracePeriodBanner />
        </div>

        {error && error.trim && error.trim() !== '' && (
          <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">
                {typeof error === 'string' ? error : JSON.stringify(error)}
              </span>
            </div>
            <button
              onClick={clearError}
              className="text-red-700 hover:text-red-900 font-medium"
            >
              {t('common.close')}
            </button>
          </div>
        )}
      </div>

      {/* コンテンツエリア - flex-1でスクロール */}
      <div className={`flex-1 min-h-0 overflow-hidden ${ganttFullscreenMode === 'none' ? 'container mx-auto px-2 sm:px-4' : 'px-4'} pt-0 sm:pt-6 pb-14 lg:pb-6`}>
        {USE_BOTTOM_PROJECT_SELECTOR ? (
          // 新UI: スマホは下部プロジェクト選択、PCは従来通り
          ganttFullscreenMode === 'full' ? (
            // 完全全画面モード: TodoList（ガント）のみ表示
            <div className="h-full overflow-hidden">
              <TodoList key="todolist-main" />
            </div>
          ) : ganttFullscreenMode === 'with-sidebar' ? (
            // サイドバー付き最大化モード: ProjectListは固定幅、TodoListが残りを使用
            <div className="flex h-full gap-2.5">
              {/* PC: ProjectListは通常モードと同程度の固定幅（約426px） */}
              <div className="hidden lg:block flex-shrink-0 h-full overflow-hidden" style={{ width: '426px' }}>
                <div className="h-full overflow-y-auto pr-2 scrollbar-thin">
                  <ProjectList />
                </div>
              </div>
              {/* TodoListは残りの幅を使用 */}
              <div className="flex-1 h-full overflow-hidden">
                <TodoList key="todolist-main" />
              </div>
            </div>
          ) : (
            // 通常モード
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 sm:gap-6 h-full">
              {/* PC: ProjectListは固定、内部でスクロール */}
              <div className="hidden lg:block lg:col-span-4 h-full overflow-hidden">
                <div className="h-full overflow-y-auto pr-2 scrollbar-thin">
                  <ProjectList />
                </div>
              </div>
              {/* TodoListは固定、内部でスクロール */}
              <div className="col-span-1 lg:col-span-8 h-full overflow-hidden">
                <TodoList key="todolist-main" />
              </div>
            </div>
          )
        ) : (
          // 旧UI: 従来通りの2カラムレイアウト
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 sm:gap-6 h-full">
            <div className="lg:col-span-4 h-full overflow-hidden">
              <div className="h-full overflow-y-auto pr-2 scrollbar-thin">
                <ProjectList />
              </div>
            </div>
            <div className="col-span-1 lg:col-span-8 h-full overflow-hidden">
              <TodoList key="todolist-main" />
            </div>
          </div>
        )}
      </div>
      
      {/* ユーザー設定モーダル */}
      <UserSettings
        isOpen={showUserSettings}
        onClose={() => setShowUserSettings(false)}
      />

      {/* お知らせモーダル */}
      <AnnouncementModal />

      {/* キーボードショートカットヘルプ */}
      <KeyboardShortcutsModal
        isOpen={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
      />

      {/* 下部プロジェクト選択 (スマホのみ) */}
      {USE_BOTTOM_PROJECT_SELECTOR && <BottomProjectSelector onOpenSettings={() => setShowUserSettings(true)} />}

      {/* Mobile Upgrade Banner (Fixed Footer) */}
      {!USE_BOTTOM_PROJECT_SELECTOR && <UpgradeBanner variant="mobile" />}

      {/* ウェルカム動画モーダル */}
      <WelcomeVideoModal
        isOpen={showWelcomeVideo}
        onClose={() => {
          setShowWelcomeVideo(false)
          // モーダルを閉じたら完了としてマーク（次回は表示しない）
          markTourCompleted(user?.id)
        }}
      />
    </div>
  )
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />
}

function App() {
  // システムのダークモード設定を検出（ネイティブアプリのみStatusBarを調整）
  useSystemDarkMode()

  // Detect site mode from environment variable
  const SITE_MODE = import.meta.env.VITE_SITE_MODE || 'app'
  const IS_LANDING_SITE = SITE_MODE === 'landing'

  // Landing site: Show LP only, redirect to app.tastack.net for login
  if (IS_LANDING_SITE) {
    return (
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/" element={<LandingPage version="v1" />} />
          <Route path="/v2" element={<LandingPage version="v2" />} />
          <Route path="/ai" element={<LandingPage version="v3" />} />
          <Route path="/team" element={<LandingPage version="v4" />} />
          <Route path="/simple" element={<LandingPage version="v5" />} />
          <Route path="/business" element={<LandingPage version="v6" />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          {/* Redirect all other paths to main landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    )
  }

  // App site: Show app dashboard
  return <AppRoutes />
}

function TeamsPage() {
  const [showSettings, setShowSettings] = useState(false)

  return (
    <>
      <Header onOpenSettings={() => setShowSettings(true)} />
      <TeamManagement />
      <UserSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </>
  )
}

function AppRoutes() {
  // Check for password recovery or email change flow from email link
  useEffect(() => {
    const hash = window.location.hash

    // Handle password recovery
    if (hash && hash.includes('type=recovery')) {
      const handlePasswordRecovery = async () => {
        try {
          const { supabase } = await import('./lib/supabase')

          // Parse the hash to get tokens
          const hashParams = new URLSearchParams(hash.substring(1))
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')

          if (accessToken && refreshToken) {
            // Set the session with recovery tokens so updateUser() can work
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            })

            if (error) {
              console.error('Password recovery session error:', error)
              alert('パスワードリセットのリンクが無効です。もう一度お試しください。')
              window.location.href = '/login'
              return
            }
          }

          // Extract invitation token if present
          const searchParams = new URLSearchParams(window.location.search)
          const invitation = searchParams.get('invitation')

          // Clear hash and redirect to reset password page
          const resetUrl = invitation
            ? `/reset-password?invitation=${invitation}`
            : '/reset-password'

          window.history.replaceState(null, '', resetUrl)
          window.location.reload()
        } catch (err) {
          console.error('Password recovery error:', err)
          alert('パスワードリセット処理中にエラーが発生しました。')
          window.location.href = '/login'
        }
      }

      handlePasswordRecovery()
      return
    }

    // Handle email change confirmation
    if (hash && hash.includes('type=email_change')) {
      // Supabase automatically handles email change when the link is clicked
      // The hash contains the tokens needed to complete the change
      // We need to process this through Supabase Auth
      const handleEmailChange = async () => {
        try {
          const { supabase } = await import('./lib/supabase')

          // Parse the hash to get tokens
          const hashParams = new URLSearchParams(hash.substring(1))
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')

          if (accessToken && refreshToken) {
            // Set the session with the new tokens
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            })

            if (error) {
              console.error('Email change error:', error)
              alert('メールアドレスの変更に失敗しました。もう一度お試しください。')
            } else {
              // Clear the hash and redirect to home
              window.history.replaceState(null, '', '/')
              alert('メールアドレスが正常に変更されました。')
              window.location.reload()
            }
          }
        } catch (err) {
          console.error('Email change processing error:', err)
        }
      }

      handleEmailChange()
    }
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <DemoProvider>
      <NetworkProvider>
        <PushNotificationProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={<LoadingSpinner size="lg" fullScreen />}>
        <Routes>
        {/* Main app dashboard - root path */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />

        {/* Redirect old /app path to root for backward compatibility */}
        <Route path="/app" element={<Navigate to="/" replace />} />

        <Route path="/login" element={<LoginForm />} />
        <Route path="/signup" element={<LoginForm />} />

        {/* Password reset page - public route */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/tasks"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <UserDashboardPage />
            </PrivateRoute>
          }
        />
        {/* 課金関連ルート: 無料モードではトップへリダイレクト */}
        <Route
          path="/pricing"
          element={
            BILLING_ENABLED ? (
              <PrivateRoute>
                <PricingPage />
              </PrivateRoute>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/billing"
          element={
            BILLING_ENABLED ? (
              <PrivateRoute>
                <BillingManagement />
              </PrivateRoute>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/referral"
          element={
            BILLING_ENABLED ? (
              <PrivateRoute>
                <ReferralProgram />
              </PrivateRoute>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/images"
          element={
            <PrivateRoute>
              <ImageManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/files"
          element={
            <PrivateRoute>
              <FileManagement />
            </PrivateRoute>
          }
        />
        {/* チーム管理: 無料・個人向けモードではトップへリダイレクト */}
        <Route
          path="/teams"
          element={
            TEAMS_ENABLED ? (
              <PrivateRoute>
                <TeamsPage />
              </PrivateRoute>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* 共有リンクの参加ページ（未ログインでも到達可能） */}
        <Route
          path="/share/:token"
          element={<ShareInvitePage />}
        />
        <Route
          path="/team/accept-invitation"
          element={<AcceptTeamInvitation />}
        />
        <Route
          path="/team-invitation"
          element={<AcceptTeamInvitation />}
        />
        <Route
          path="/admin/tier-settings"
          element={
            <PrivateRoute>
              <AdminTierSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/contact-management"
          element={
            <PrivateRoute>
              <AdminContactPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/ai-usage"
          element={
            <PrivateRoute>
              <AdminAIUsagePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/ai-settings"
          element={
            <PrivateRoute>
              <AdminAISettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/static-content"
          element={
            <PrivateRoute>
              <AdminStaticContentPage />
            </PrivateRoute>
          }
        />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/commercial-transactions-act" element={<CommercialTransactionsActPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/videos" element={<HelpVideosPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        {/* GanttChart V2 - 開発中 (feature/milestone-gantt) */}
        <Route
          path="/gantt-v2"
          element={
            <PrivateRoute>
              <GanttChartV2Page />
            </PrivateRoute>
          }
        />
        {/* デモモード - ログイン不要、本番DBに影響なし */}
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/demo/:type" element={<DemoPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
        </Suspense>
        </Router>
        </PushNotificationProvider>
      </NetworkProvider>
      </DemoProvider>
    </QueryClientProvider>
  )
}

export default App